import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { 
  Plug, 
  Plus, 
  Power, 
  PowerOff,
  TestTube,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Key,
  X,
  Search,
  Building,
  User,
  Mail,
  MapPin,
  Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Apollo Search Modal Component
const ApolloSearchModal = ({ isOpen, onClose }) => {
  const [searchParams, setSearchParams] = useState({
    q_organization_domains: '',
    q_keywords: '',
    person_titles: '',
    person_locations: '',
    per_page: 5
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);

    try {
      // Build payload - convert comma-separated strings to arrays
      const payload = {
        per_page: searchParams.per_page
      };

      if (searchParams.q_organization_domains.trim()) {
        payload.q_organization_domains = searchParams.q_organization_domains.trim();
      }
      if (searchParams.q_keywords.trim()) {
        payload.q_keywords = searchParams.q_keywords.trim();
      }
      if (searchParams.person_titles.trim()) {
        payload.person_titles = searchParams.person_titles.split(',').map(t => t.trim()).filter(Boolean);
      }
      if (searchParams.person_locations.trim()) {
        payload.person_locations = searchParams.person_locations.split(',').map(l => l.trim()).filter(Boolean);
      }

      const response = await axios.post(
        `${API_URL}/api/admin/integrations/apollo/test-search`,
        payload
      );

      if (response.data.success) {
        setResults(response.data);
      } else {
        setError(response.data.message || 'Search failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Search request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchParams({
      q_organization_domains: '',
      q_keywords: '',
      person_titles: '',
      person_locations: '',
      per_page: 5
    });
    setResults(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <Search className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Apollo Contact Search</h2>
              <p className="text-sm text-slate-400">Search for contacts using Apollo.io API</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="space-y-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  Organization Domains
                  <span className="text-slate-500 text-xs ml-2">(comma-separated)</span>
                </label>
                <input
                  type="text"
                  data-testid="apollo-org-domains"
                  value={searchParams.q_organization_domains}
                  onChange={(e) => setSearchParams({...searchParams, q_organization_domains: e.target.value})}
                  placeholder="e.g., google.com, microsoft.com"
                  className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Search className="w-4 h-4 inline mr-2" />
                  Keywords
                </label>
                <input
                  type="text"
                  data-testid="apollo-keywords"
                  value={searchParams.q_keywords}
                  onChange={(e) => setSearchParams({...searchParams, q_keywords: e.target.value})}
                  placeholder="e.g., engineering, sales"
                  className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-2" />
                  Person Titles
                  <span className="text-slate-500 text-xs ml-2">(comma-separated)</span>
                </label>
                <input
                  type="text"
                  data-testid="apollo-titles"
                  value={searchParams.person_titles}
                  onChange={(e) => setSearchParams({...searchParams, person_titles: e.target.value})}
                  placeholder="e.g., CEO, CTO, VP Engineering"
                  className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Person Locations
                  <span className="text-slate-500 text-xs ml-2">(comma-separated)</span>
                </label>
                <input
                  type="text"
                  data-testid="apollo-locations"
                  value={searchParams.person_locations}
                  onChange={(e) => setSearchParams({...searchParams, person_locations: e.target.value})}
                  placeholder="e.g., United States, California"
                  className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-400">Results per page:</label>
                <select
                  value={searchParams.per_page}
                  onChange={(e) => setSearchParams({...searchParams, per_page: parseInt(e.target.value)})}
                  className="px-3 py-1.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                >
                  <option value={1}>1</option>
                  <option value={3}>3</option>
                  <option value={5}>5</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="apollo-search-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Search Contacts
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-medium">Search Failed</p>
                <p className="text-red-400/80 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              {/* Results Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Search Results
                </h3>
                <span className="text-sm text-slate-400">
                  Showing {results.contacts.length} of {results.total_results?.toLocaleString() || 0} total
                </span>
              </div>

              {/* No Results */}
              {results.contacts.length === 0 ? (
                <div className="text-center py-8 bg-slate-900/30 rounded-lg border border-slate-700/50">
                  <Search className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No results found</p>
                  <p className="text-slate-500 text-sm mt-1">Try adjusting your search criteria</p>
                </div>
              ) : (
                /* Contact Cards */
                <div className="space-y-3">
                  {results.contacts.map((contact, index) => (
                    <div
                      key={contact.id || index}
                      className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4 hover:border-slate-600/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Name */}
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="font-semibold text-white">
                              {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown'}
                            </span>
                          </div>
                          
                          {/* Title */}
                          {contact.title && (
                            <div className="flex items-center gap-2 mb-1">
                              <Briefcase className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">{contact.title}</span>
                            </div>
                          )}
                          
                          {/* Organization */}
                          {contact.organization_name && (
                            <div className="flex items-center gap-2 mb-1">
                              <Building className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-400">{contact.organization_name}</span>
                            </div>
                          )}
                        </div>

                        {/* Data Availability Indicators */}
                        <div className="flex flex-wrap gap-2 ml-4">
                          {contact.has_email && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                              <Mail className="w-3 h-3" />
                              Email Available
                            </span>
                          )}
                          {(contact.has_city || contact.has_state || contact.has_country) && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                              <MapPin className="w-3 h-3" />
                              Location Available
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <p className="text-xs text-slate-500 text-center">
            Powered by Apollo.io API • Results limited to first 5 contacts • Use People Enrichment API to get full contact details
          </p>
        </div>
      </div>
    </div>
  );
};

const AdminIntegrations = () => {
  const { user, isOwner, isAdmin } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [testingId, setTestingId] = useState(null);
  
  // Apollo Search Modal state
  const [showApolloModal, setShowApolloModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({ name: '', api_key: '' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    try {
      if (editingId) {
        // Update existing
        const updateData = { name: formData.name };
        if (formData.api_key) {
          updateData.api_key = formData.api_key;
        }
        await axios.put(`${API_URL}/api/admin/integrations/${editingId}`, updateData);
      } else {
        // Create new
        await axios.post(`${API_URL}/api/admin/integrations`, formData);
      }
      
      setShowAddForm(false);
      setEditingId(null);
      setFormData({ name: '', api_key: '' });
      fetchIntegrations();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to save integration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/admin/integrations/${id}/toggle`);
      fetchIntegrations();
    } catch (error) {
      console.error('Error toggling integration:', error);
    }
  };

  const handleTest = async (id, integrationName) => {
    // Check if this is Apollo integration - open modal instead
    if (integrationName.toLowerCase() === 'apollo') {
      setShowApolloModal(true);
      return;
    }
    
    // Regular test for other integrations
    setTestingId(id);
    setTestResults(prev => ({ ...prev, [id]: null }));
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/integrations/${id}/test`);
      setTestResults(prev => ({ ...prev, [id]: response.data }));
    } catch (error) {
      setTestResults(prev => ({ 
        ...prev, 
        [id]: { success: false, message: error.response?.data?.detail || 'Test failed' }
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/integrations/${id}`);
      fetchIntegrations();
    } catch (error) {
      console.error('Error deleting integration:', error);
    }
  };

  const handleEdit = (integration) => {
    setFormData({ name: integration.name, api_key: '' });
    setEditingId(integration.id);
    setShowAddForm(true);
    setFormError('');
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({ name: '', api_key: '' });
    setFormError('');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Apollo Search Modal */}
      <ApolloSearchModal 
        isOpen={showApolloModal} 
        onClose={() => setShowApolloModal(false)} 
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="integrations-title">
            Integrations
          </h1>
          <p className="text-slate-400">
            Manage third-party API integrations and credentials
          </p>
        </div>
        {isAdmin() && !showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            data-testid="add-integration-btn"
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Integration
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit Integration' : 'Add New Integration'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {formError}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Integration Name
                </label>
                <input
                  type="text"
                  data-testid="integration-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Stripe, OpenAI, SendGrid"
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  API Key {editingId && <span className="text-slate-500">(leave empty to keep current)</span>}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    data-testid="integration-apikey-input"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder="sk_live_..."
                    required={!editingId}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                  />
                  <Key className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="integration-submit-btn"
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                ) : (
                  <>{editingId ? 'Update' : 'Create'} Integration</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Integrations List */}
      {integrations.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-12 text-center">
          <Plug className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Integrations Yet</h3>
          <p className="text-slate-400 mb-4">
            Add your first integration to start managing API credentials.
          </p>
          {isAdmin() && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Integration
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {integrations.map((integration) => {
            const isProvider = !!integration.status;
            const operational = isProvider ? integration.status === 'ready' : integration.enabled;
            const statusText = isProvider
              ? ({ ready: 'Connected', token_pending: 'Token Pending', not_configured: 'Not Configured' }[integration.status] || integration.status)
              : (integration.enabled ? 'Enabled' : 'Disabled');
            return (
            <div
              key={integration.id}
              data-testid={`integration-${integration.id}`}
              className={`bg-slate-800/50 border rounded-xl p-6 transition-all ${
                operational 
                  ? 'border-slate-700/50 hover:border-slate-600/50' 
                  : 'border-red-500/20 bg-red-500/5'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Status Indicator */}
                  <div className={`p-3 rounded-lg ${
                    operational 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {operational ? <Power className="w-6 h-6" /> : <PowerOff className="w-6 h-6" />}
                  </div>
                  
                  {/* Integration Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      {integration.name}
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        operational 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {statusText}
                      </span>
                      {integration.name.toLowerCase() === 'apollo' && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-orange-500/20 text-orange-400">
                          Search Available
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-sm text-slate-400 bg-slate-900/50 px-2 py-1 rounded font-mono">
                        {integration.api_key}
                      </code>
                      {!isOwner() && (
                        <span className="text-xs text-slate-500">(masked)</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Updated: {new Date(integration.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {/* Test Result */}
                  {testResults[integration.id] && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      testResults[integration.id].success 
                        ? 'bg-emerald-500/10 text-emerald-400' 
                        : 'bg-red-500/10 text-red-400'
                    }`}>
                      {testResults[integration.id].success ? (
                        <><CheckCircle className="w-4 h-4" /> Success</>
                      ) : (
                        <><XCircle className="w-4 h-4" /> Failed</>
                      )}
                    </div>
                  )}

                  {/* Test Button */}
                  <button
                    onClick={() => handleTest(integration.id, integration.name)}
                    disabled={testingId === integration.id}
                    data-testid={`test-integration-${integration.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg disabled:opacity-50 transition-colors ${
                      integration.name.toLowerCase() === 'apollo'
                        ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                        : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                    }`}
                  >
                    {testingId === integration.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : integration.name.toLowerCase() === 'apollo' ? (
                      <Search className="w-4 h-4" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                    {integration.name.toLowerCase() === 'apollo' ? 'Search' : 'Test'}
                  </button>

                  {/* Toggle Button — hidden for provider-type integrations whose
                      operational status is derived from configuration, not this flag */}
                  {!isProvider && (
                  <button
                    onClick={() => handleToggle(integration.id)}
                    data-testid={`toggle-integration-${integration.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      integration.enabled
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                    }`}
                  >
                    {integration.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    {integration.enabled ? 'Disable' : 'Enable'}
                  </button>
                  )}

                  {/* Edit/Delete (Owner and Admin) */}
                  {isAdmin() && (
                    <>
                      <button
                        onClick={() => handleEdit(integration)}
                        data-testid={`edit-integration-${integration.id}`}
                        className="flex items-center gap-2 px-3 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(integration.id, integration.name)}
                        data-testid={`delete-integration-${integration.id}`}
                        className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Role Info */}
      <div className="mt-8 p-4 bg-slate-800/30 border border-slate-700/30 rounded-lg">
        <p className="text-sm text-slate-500">
          {isOwner() ? (
            <>As an <strong className="text-amber-400">Owner</strong>, you can view full API keys and manage all integrations.</>
          ) : (
            <>As an <strong className="text-blue-400">Admin</strong>, you can create, edit, delete, toggle, and test integrations. API keys are masked for security - only Owners can view full keys.</>
          )}
        </p>
      </div>
    </AdminLayout>
  );
};

export default AdminIntegrations;
